<template>
  <DialogRoot
    v-model:open="open"
    :class="[rootClass, zClass]"
    :modal="ignorePosDialog || !showPinDialog"
  >
    <DialogPortal>
      <DialogOverlay class="fixed inset-0 backdrop-blur" :class="[backdropClass, zClass]" />
      <DialogContent
        class="fixed top-1/2 left-1/2 flex max-h-[calc(100dvh-32px)] w-full max-w-3xl -translate-x-1/2 -translate-y-1/2 transform flex-col rounded-lg p-8 shadow-lg sm:w-[calc(100%-32px)]"
        :class="[contentClass, zClass]"
        @interact-outside="(event) => required && event.preventDefault()"
        @escape-key-down="(event) => required && event.preventDefault()"
        @open-auto-focus="(event) => preventAutoFocus && event.preventDefault()"
      >
        <DialogTitle class="text-xl font-bold" v-if="$slots.title || title">
          <slot name="title">{{ title }}</slot>
        </DialogTitle>
        <DialogDescription class="my-4 text-sm" v-if="$slots.description || description">
          <slot name="description">{{ description }}</slot>
        </DialogDescription>
        <div class="flex min-h-0 grow flex-col">
          <div
            v-if="$slots.actions && tabOrderActionsFirst"
            class="order-2 mt-4 flex flex-wrap gap-2"
            :class="{ 'flex-row-reverse justify-start': reverseActionsOrder }"
          >
            <slot name="actions" />
          </div>
          <div class="order-1 -mx-8 min-h-0 grow overflow-x-hidden overflow-y-auto px-8 py-2">
            <slot />
          </div>
          <div
            v-if="$slots.actions && !tabOrderActionsFirst"
            class="order-2 mt-4 flex flex-wrap gap-2"
            :class="{ 'flex-row-reverse justify-start': reverseActionsOrder }"
          >
            <slot name="actions" />
          </div>
        </div>
        <DialogClose class="btn btn-ghost btn-circle absolute top-2 right-2 z-10" v-if="!required">
          <FontAwesomeIcon :icon="faXmark" />
        </DialogClose>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>

<script setup lang="ts">
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
} from 'reka-ui';
import { useAuth } from '../../stores/authStore';

export interface PopupDialogOptions {
  required?: boolean;
  title?: string;
  description?: string;
  rootClass?: string;
  contentClass?: string;
  backdropClass?: string;
  tabOrderActionsFirst?: boolean;
  reverseActionsOrder?: boolean;
  zClass?: string;
  ignorePosDialog?: boolean;
  preventAutoFocus?: boolean;
}

withDefaults(defineProps<PopupDialogOptions>(), {
  noClose: false,
  title: '',
  description: '',
  zClass: 'z-30',
  contentClass: 'bg-base-100',
  backdropClass: 'bg-black/50',
  preventAutoFocus: false,
});

const { showPinDialog } = useAuth();

const open = defineModel('open', {
  type: Boolean,
  default: false,
});
</script>
